import React from 'react';

import PropTypes from 'prop-types';

import { noop, stylePropType } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import { getSelectedDates } from '../helpers';
import useCalendars from '../hooks/useCalendars';
import useDatePicker from '../hooks/useDatePicker';
import useNavigation from '../hooks/useNavigation';
import useRangeHover from '../hooks/useRangeHover';
import useSelection from '../hooks/useSelection';
import * as types from '../types';
import Calendar from './Calendar';
import DefaultFooter from './DefaultFooter';
import DefaultFooterActions from './DefaultFooterActions';

const DATE_PICKER_CALENDAR_WIDTH = 321;

type OverlayProps = Pick<ReturnType<typeof useDatePicker>, 'toggleOverlay'> &
  Pick<
    ReturnType<typeof useSelection>,
    'onDateSelected' | 'resetSelection' | 'startDate' | 'endDate' | 'date'
  > &
  Pick<
    types.DatePickerArgs,
    | 'range'
    | 'minDate'
    | 'maxDate'
    | 'double'
    | 'onMonthChange'
    | 'firstDayOfWeek'
    | 'showOutsideDays'
    | 'isDateSelectable'
    | 'yearsToDisplay'
    | 'calendarProps'
    | 'hasEventForDate'
    | 'tooltipContentForDate'
    | 'renderFooter'
    | 'renderFooterActions'
  > &
  types.OverlayProps;

const Overlay = React.forwardRef<HTMLDivElement, OverlayProps>(
  (
    {
      date,
      double,
      calendarProps,
      range,
      startDate,
      endDate,
      minDate,
      maxDate,
      resetSelection,
      onDateSelected,
      onMonthChange = noop,
      firstDayOfWeek = 0,
      showOutsideDays = false,
      isDateSelectable,
      yearsToDisplay = 20,
      toggleOverlay,
      hasEventForDate,
      tooltipContentForDate,
      renderFooter = DefaultFooter,
      renderFooterActions = DefaultFooterActions,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const [stateHoveredDate, onDateHover] = useRangeHover({
      range,
      startDate,
      endDate,
      minDate,
      maxDate,
    });

    const {
      activeMonths,
      updateActiveMonths,
      setMonth,
      setYear,
      nextMonth,
      prevMonth,
      resetViewToTodayAndSelect,
    } = useNavigation({
      range,
      date,
      startDate,
      endDate,
      numberOfMonths: double ? 2 : 1,
      onDateSelected,
      onMonthChange,
      minDate,
      maxDate,
    });
    const selectedDates = getSelectedDates({ range, date, startDate, endDate });
    const calendars = useCalendars({
      activeMonths,
      selectedDates,
      minDate,
      maxDate,
      firstDayOfWeek,
      showOutsideDays,
      isDateSelectable,
      yearsToDisplay,
    });

    const totalCalendars = calendars.length;

    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            minHeight: 321,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.colors.white,
            boxShadow: theme.shadows.highElevation,
            borderRadius: 6,
            padding: '16px 0',
            width: totalCalendars * DATE_PICKER_CALENDAR_WIDTH,
          },
          style,
        ]}
        {...rest}
      >
        <div css={{ display: 'flex' }}>
          {calendars.map((calendar, key) => {
            return (
              <Calendar
                key={key}
                range={range}
                calendar={calendar}
                calendars={calendars}
                stateHoveredDate={stateHoveredDate}
                onDateHover={onDateHover}
                setMonth={setMonth}
                setYear={setYear}
                nextMonth={nextMonth}
                prevMonth={prevMonth}
                onDateSelected={onDateSelected}
                updateActiveMonths={updateActiveMonths}
                hasEventForDate={hasEventForDate}
                tooltipContentForDate={tooltipContentForDate}
                startDate={startDate}
                endDate={endDate}
                {...calendarProps}
              />
            );
          })}
        </div>
        {renderFooter?.({
          today: resetViewToTodayAndSelect,
          reset: resetSelection,
          toggleOverlay,
          renderFooterActions,
        })}
      </div>
    );
  }
);

Overlay.displayName = 'Overlay';

Overlay.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether the we should display double date picker */
  double: PropTypes.bool,
  onDateSelected: PropTypes.func.isRequired,
  resetSelection: PropTypes.func.isRequired,
  toggleOverlay: PropTypes.func.isRequired,
  /** A selected date for a calendar without range */
  date: PropTypes.instanceOf(Date),
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Flag whether the date picker is for a date range of a single value */
  range: PropTypes.bool,
  /** A callbcack function called when the selected date(s) has been changed */
  onChange: PropTypes.func,
  /** A callbcack function called when the active months have changed (arrow click, month/year selection) */
  onMonthChange: PropTypes.func,
  /** Handler to manually check if date is selectable */
  isDateSelectable: PropTypes.func,
  /** First day of week to display */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /** Fill the blanks with days from prev/next month */
  showOutsideDays: PropTypes.bool,
  /* Props passed to each calendar */
  calendarProps: PropTypes.object,
  /** Render custom footer, executed in DatePickerOverlay */
  renderFooter: PropTypes.func,
  /** Render custom footer actions, executed in DatePickerOverlay */
  renderFooterActions: PropTypes.func,
  /** Determines if Day Cell should render an event dot */
  hasEventForDate: PropTypes.func,
  /** Tooltip content for Day Cell */
  tooltipContentForDate: PropTypes.func,
  /** How much years to display under and above current year, defaults to +-20 */
  yearsToDisplay: PropTypes.number,
};

export default Overlay;
